import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import Preloader from '../../components/Preloader';
import api from '../../services/api';
import { Link, useLocation } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Badge, Row, Col } from 'react-bootstrap';

const DoctorBatchCases = () => {
  const { user } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const batchIdFromUrl = params.get('batch'); // Get batch ID if doctor is coming from completed case
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDeadlineWarning, setBatchDeadlineWarning] = useState(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [batchCases, setBatchCases] = useState([]);
  const [caseStats, setCaseStats] = useState({ total: 0, completed: 0, incomplete: 0 });
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadMyBatches();
    }
  }, [user]);

  // Refresh case list when user returns to this page (after completing questions)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedBatch && selectedProtocol) {
        // Page became visible again, refresh to get latest case status
        console.log('Page became visible, refreshing case list...');
        loadBatchCases(selectedBatch.id, selectedProtocol.id);
      }
    };

    const handleFocus = () => {
      if (selectedBatch && selectedProtocol) {
        // Window gained focus, refresh to get latest case status
        console.log('Window gained focus, refreshing case list...');
        loadBatchCases(selectedBatch.id, selectedProtocol.id);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [selectedBatch, selectedProtocol]);

  const loadMyBatches = async () => {
    try {
      setLoading(true);
      console.log('[DEBUG] Loading my batches...');
      const response = await api.get('/batches/my-batches');
      const batchesData = response.data.batches || [];
      console.log('[DEBUG] Loaded batches:', batchesData);
      
      // Check if time_remaining is included in each batch
      batchesData.forEach((batch, index) => {
        console.log(`[DEBUG] Batch ${index + 1} (${batch.name}): time_remaining =`, batch.time_remaining);
      });
      
      setBatches(batchesData);
      
      // Check if doctor is returning from a completed case with a batch ID
      let batchToSelect = null;
      
      if (batchIdFromUrl) {
        // Try to find the batch by ID or name (case-insensitive)
        batchToSelect = batchesData.find(batch => 
          batch.id == batchIdFromUrl || 
          batch.name?.toLowerCase() === decodeURIComponent(batchIdFromUrl).toLowerCase()
        );
        if (batchToSelect) {
          console.log('[DEBUG] Auto-selecting batch from URL parameter:', batchToSelect.name);
        }
      }
      
      // If no batch found from URL, auto-select the first active batch
      if (!batchToSelect) {
        batchToSelect = batchesData.find(batch => {
          const status = getBatchStatus(batch);
          return status === 'active' || status === 'grace_period';
        });
        if (batchToSelect) {
          console.log('[DEBUG] Auto-selecting active batch:', batchToSelect.name);
        } else {
          console.log('[DEBUG] No active batches found');
        }
      }
      
      if (batchToSelect) {
        setSelectedBatch(batchToSelect);
        if (batchToSelect.protocols && batchToSelect.protocols.length > 0) {
          setSelectedProtocol(batchToSelect.protocols[0]);
          loadBatchCases(batchToSelect.id, batchToSelect.protocols[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBatchCases = async (batchId, protocolId) => {
    try {
      setLoading(true);
      
      // Add timestamp to prevent caching and ensure fresh data
      const response = await api.get(`/batches/${batchId}/protocol/${protocolId}/cases?_t=${Date.now()}`);
      const casesData = response.data.cases || [];
      
      // Log response data for debugging
      console.log('[DEBUG] Batch cases response:', response.data);
      console.log('[DEBUG] Total questions in protocol:', response.data.total_questions_in_protocol);
      
      // Update case statistics - use doctor-specific counts
      const stats = {
        total: response.data.total_cases || 0,
        completed: response.data.my_completed_cases || response.data.completed_cases || 0,
        incomplete: response.data.my_incomplete_cases || response.data.incomplete_cases || 0
      };
      setCaseStats(stats);
      
      // Enhance cases with protocol info
      const enhancedCases = casesData.map(caseItem => {
        return {
          ...caseItem,
          protocol: selectedProtocol
        };
      });

      setBatchCases(enhancedCases);
      
      console.log(`Loaded ${enhancedCases.length} incomplete cases for batch ${batchId}, protocol ${protocolId}`);
      console.log(`Stats: ${stats.total} total, ${stats.completed} completed, ${stats.incomplete} incomplete`);
    } catch (error) {
      console.error('Error loading batch cases:', error);
      setBatchCases([]);
      setCaseStats({ total: 0, completed: 0, incomplete: 0 });
    } finally {
      setLoading(false);
    }
  };

  const getBatchStatus = (batch) => {
    const now = new Date();
    const startDate = new Date(batch.start_date);
    const endDate = batch.extended_end_date ? new Date(batch.extended_end_date) : new Date(batch.end_date);
    const graceDate = new Date(endDate.getTime() + (batch.grace_period_hours || 12) * 60 * 60 * 1000);
    
    if (now < startDate) {
      return 'future';
    } else if (now > graceDate) {
      return 'expired';
    } else if (now > endDate) {
      return 'grace_period';
    } else {
      return 'active';
    }
  };

  const getBatchStatusInfo = (status) => {
    switch (status) {
      case 'future':
        return { text: 'Future', class: 'text-secondary' };
      case 'expired':
        return { text: 'Expired', class: 'text-danger' };
      case 'grace_period':
        return { text: 'Grace Period', class: 'text-warning' };
      case 'active':
        return { text: 'Active', class: 'text-success' };
      default:
        return { text: 'Unknown', class: 'text-secondary' };
    }
  };

  const handleBatchSelect = (batch) => {
    setSelectedBatch(batch);
    setSelectedProtocol(null);
    setBatchCases([]);
    
    // Calculate deadline warning for selected batch
    const deadlineInfo = getDeadlineInfo(batch);
    setBatchDeadlineWarning(deadlineInfo);
    
    if (batch.protocols && batch.protocols.length > 0) {
      // Auto-select first protocol if only one, otherwise show selection
      if (batch.protocols.length === 1) {
        setSelectedProtocol(batch.protocols[0]);
        loadBatchCases(batch.id, batch.protocols[0].id);
      }
    }
  };

  const getDeadlineInfo = (batch) => {
    const now = new Date();
    const startDate = new Date(batch.start_date);
    const endDate = batch.extended_end_date ? new Date(batch.extended_end_date) : new Date(batch.end_date);
    const graceDate = new Date(endDate.getTime() + (batch.grace_period_hours || 12) * 60 * 60 * 1000);
    
    if (now < startDate) {
      return {
        text: `Starts ${startDate.toLocaleDateString()}`,
        type: 'future',
        variant: 'info',
        icon: 'fa-calendar'
      };
    } else if (now > graceDate) {
      const expiredHours = Math.floor((now - graceDate) / (1000 * 60 * 60));
      return {
        text: `Expired ${expiredHours}h ago`,
        type: 'expired',
        variant: 'danger',
        icon: 'fa-times-circle'
      };
    } else if (now > endDate) {
      // Use backend's time_remaining if available, otherwise calculate
      const timeRemaining = batch.time_remaining;
      console.log(`[DEBUG] Grace period - timeRemaining from backend: ${timeRemaining}`, batch);
      if (timeRemaining) {
        return {
          text: `${timeRemaining} (Grace Period)`,
          type: 'grace',
          variant: 'warning',
          icon: 'fa-exclamation-triangle'
        };
      } else {
        const remainingHours = Math.floor((graceDate - now) / (1000 * 60 * 60));
        return {
          text: `${remainingHours}h left (Grace Period)`,
          type: 'grace',
          variant: 'warning',
          icon: 'fa-exclamation-triangle'
        };
      }
    } else {
      // Use backend's time_remaining for consistency with medical admin interface
      const timeRemaining = batch.time_remaining;
      console.log(`[DEBUG] Active batch - timeRemaining from backend: ${timeRemaining}`, batch);
      if (timeRemaining) {
        return {
          text: `${timeRemaining}`,
          type: 'active',
          variant: 'success',
          icon: 'fa-clock'
        };
      } else {
        // Fallback to frontend calculation if backend data unavailable
        console.log(`[DEBUG] No timeRemaining from backend, using frontend calculation`, batch);
        const remainingHours = Math.floor((endDate - now) / (1000 * 60 * 60));
        const remainingDays = Math.floor(remainingHours / 24);
        const hoursRemainder = remainingHours % 24;
        
        let timeText;
        if (remainingDays > 0) {
          timeText = `${remainingDays} day${remainingDays !== 1 ? 's' : ''} ${hoursRemainder > 0 ? `and ${hoursRemainder}h` : ''} remaining`;
        } else {
          timeText = `${remainingHours}h remaining`;
        }
        
        return {
          text: timeText,
          type: 'active',
          variant: remainingHours <= 24 ? 'warning' : 'success',
          icon: remainingHours <= 24 ? 'fa-exclamation-triangle' : 'fa-clock'
        };
      }
    }
  };

  const handleProtocolSelect = (protocol) => {
    setSelectedProtocol(protocol);
    if (selectedBatch) {
      loadBatchCases(selectedBatch.id, protocol.id);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    
    try {
      const date = new Date(dateString);
      return date instanceof Date && !isNaN(date) ?
        date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Africa/Nairobi'  // EAT timezone (East Africa Time)
        }) :
        'Invalid date format';
    } catch (error) {
      return 'Date error';
    }
  };

  const getEffectiveEndDate = (batch) => batch.extended_end_date || batch.end_date;

  const refreshCaseList = async () => {
    if (selectedBatch && selectedProtocol) {
      setRefreshing(true);
      await loadBatchCases(selectedBatch.id, selectedProtocol.id);
      setRefreshing(false);
    }
  };

  const isBatchAccessible = (batch) => {
    const status = getBatchStatus(batch);
    return ['active', 'grace_period'].includes(status);
  };

  if (loading && batches.length === 0) {
    return (
      <ResearchLayout pageTitle="My Batch Cases">
        <Preloader message="Loading your batch assignments..." />
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="My Batch Cases">
      <div className="container-fluid">

        <Row>
          {/* Batch Selection */}
          <div className="col-md-4">
            <Card className="mb-4">
              <Card.Header className="bg-secondary text-white">
                <h5 className="mb-0">My Batches ({batches.length})</h5>
              </Card.Header>
              <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {batches.length === 0 ? (
                  <p className="text-muted text-center">No batches assigned</p>
                ) : (
                  <div className="list-group list-group-flush">
                    {batches.map(batch => {
                      const status = getBatchStatus(batch);
                      const statusInfo = getBatchStatusInfo(status);
                      const isAccessible = isBatchAccessible(batch);
                      
                      return (
                        <div
                          key={batch.id}
                          className={`list-group-item list-group-item-action ${selectedBatch?.id === batch.id ? 'active' : ''} ${!isAccessible ? 'opacity-50' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleBatchSelect(batch)}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="mb-1">{batch.name}</h6>
                              <p className="mb-1 small text-muted">
                                {batch.protocol_count || 0} protocols • {batch.case_count || 0} cases
                              </p>
                               <small>
                                 {formatDate(batch.start_date)} - {formatDate(getEffectiveEndDate(batch))}
                               </small>
                            </div>
                            <div className="text-end">
                              <Badge 
                                bg={status === 'active' ? 'success' : 
                                    status === 'grace_period' ? 'warning' : 
                                    status === 'expired' ? 'danger' : 'secondary'}
                              >
                                {statusInfo.text}
                              </Badge>
                              {!isAccessible && (
                                <div className="mt-1">
                                  <small className="text-muted">
                                    <i className="fas fa-lock me-1"></i>
                                    Access limited
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Protocol and Case Selection */}
          <div className="col-md-8">
            {selectedBatch ? (
              <div>
                {/* Protocol Selection */}
                {selectedBatch.protocols && selectedBatch.protocols.length > 0 && (
                  <Card className="mb-4">
                    {/* Deadline Banner at Top */}
                    {batchDeadlineWarning && (
                      <div className={`alert alert-${batchDeadlineWarning.variant} border-0 shadow-sm mb-0`} role="alert">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <i className={`fas ${batchDeadlineWarning.icon} me-3`}></i>
                            <div>
                              <strong>Batch Deadline:</strong> {batchDeadlineWarning.text}
                              {batchDeadlineWarning.type === 'grace' && (
                                <p className="mb-0 mt-1">Grace period active - complete remaining cases</p>
                              )}
                              {batchDeadlineWarning.type === 'expired' && (
                                <p className="mb-0 mt-1">Batch expired - completed work saved</p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="outline-light btn-sm"
                            onClick={() => {
                              const updatedBatch = { ...selectedBatch };
                              // Force re-calculate deadline
                              const deadlineInfo = getDeadlineInfo(updatedBatch);
                              setBatchDeadlineWarning(deadlineInfo);
                            }}
                            title="Refresh deadline calculation"
                          >
                            <i className="fas fa-sync-alt me-1"></i>
                            Refresh
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0">Select Protocol</h5>
                          <p className="text-muted mb-0">
                            Batch: <strong>{selectedBatch.name}</strong>
                            {getEffectiveEndDate(selectedBatch) && (
                              <span className="ms-2">
                                <i className="fas fa-calendar-alt me-1"></i>
                                Deadline: {formatDate(getEffectiveEndDate(selectedBatch))}
                              </span>
                            )}
                          </p>
                        </div>
                        <Button
                          variant="outline-primary btn-sm"
                          onClick={async () => {
                            console.log('[DEBUG] Refreshing batches with cache busting');
                            // Add timestamp to bust cache
                            const timestamp = new Date().getTime();
                            const response = await api.get(`/batches/my-batches?_t=${timestamp}`);
                            const batchesData = response.data.batches || [];
                            console.log('[DEBUG] Refreshed batches:', batchesData);
                            
                            // Update the selected batch if it exists in the new data
                            const updatedSelectedBatch = batchesData.find(b => b.id === selectedBatch.id);
                            if (updatedSelectedBatch) {
                              setSelectedBatch(updatedSelectedBatch);
                              // Recalculate deadline info
                              const deadlineInfo = getDeadlineInfo(updatedSelectedBatch);
                              setBatchDeadlineWarning(deadlineInfo);
                            }
                            
                            setBatches(batchesData);
                          }}
                          title="Refresh batch list"
                        >
                          <i className="fas fa-sync-alt me-1"></i>
                          Refresh Batches
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {selectedBatch.protocols.length === 1 ? (
                        <div className="alert alert-info">
                          <strong>Single Protocol:</strong> {selectedBatch.protocols[0].name}
                          {selectedBatch.protocols[0].case_count && (
                            <span className="ms-3">
                              <Badge bg="primary">{selectedBatch.protocols[0].case_count} cases</Badge>
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="row">
                          {selectedBatch.protocols.map(protocol => (
                            <div key={protocol.id} className="col-md-6 mb-3">
                              <Card
                                className={`h-100 ${selectedProtocol?.id === protocol.id ? 'border-primary' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleProtocolSelect(protocol)}
                              >
                                <Card.Body>
                                  <h6>{protocol.name}</h6>
                                  <p className="text-muted small mb-2">
                                    {protocol.description || 'No description available'}
                                  </p>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <Badge bg="primary">
                                      {protocol.case_count || 0} cases
                                    </Badge>
                                    {selectedProtocol?.id === protocol.id && (
                                      <i className="fas fa-check-circle text-primary"></i>
                                    )}
                                  </div>
                                </Card.Body>
                              </Card>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}

                {/* Cases Display */}
                {selectedProtocol && (
                  <Card>
                    <Card.Header className="bg-warning text-dark">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0">
                            <i className="fas fa-folder me-2"></i>
                            {selectedProtocol.name} Cases
                          </h5>
                          <small className="d-block mt-1 opacity-75">
                            Batch: {selectedBatch.name} • Protocol: {selectedProtocol.name}
                          </small>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          {caseStats.total > 0 && (
                            <div className="text-end">
                              <div className="h5 mb-0 text-warning">
                                {caseStats.incomplete} remaining
                              </div>
                              <small className="opacity-75">
                                of {caseStats.total} total cases
                              </small>
                            </div>
                          )}
                          <Button
                            variant="outline-light btn-sm"
                            onClick={refreshCaseList}
                            disabled={refreshing || loading}
                            title="Refresh case list"
                          >
                            <i className={`fas ${refreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-1`}></i>
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                          </Button>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {loading ? (
                        <div className="text-center">
                          <Spinner animation="border" />
                          <p className="mt-2">Loading cases...</p>
                        </div>
                      ) : caseStats.total === 0 ? (
                        <div className="text-center py-5">
                          <i className="fas fa-folder-open fa-4x text-muted mb-4"></i>
                          <h4 className="text-muted">No Cases Available</h4>
                          <p className="text-muted">
                            No cases are currently assigned to this protocol in this batch.
                          </p>
                        </div>
                      ) : caseStats.incomplete === 0 ? (
                        <div className="text-center py-5">
                          <i className="fas fa-check-circle fa-4x text-success mb-4"></i>
                          <h4 className="text-success">All Cases Completed!</h4>
                          <p className="text-muted">
                            You have completed all {caseStats.total} cases in this protocol.
                          </p>
                          {caseStats.completed > 0 && (
                            <div className="mt-3">
                              <Badge bg="success" className="p-2">
                                <i className="fas fa-check me-1"></i>
                                {caseStats.completed} cases completed
                              </Badge>
                            </div>
                          )}
                        </div>
                      ) : batchCases.length > 0 ? (
                        <>
                          {/* Progress summary */}
                          {caseStats.total > 0 && (
                            <div className="mb-4">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-muted small">Progress</span>
                                <span className="text-muted small">
                                  {caseStats.completed} completed • {caseStats.incomplete} remaining
                                </span>
                              </div>
                              <div className="progress" style={{ height: '8px' }}>
                                <div
                                  className="progress-bar bg-success"
                                  role="progressbar"
                                  style={{
                                    width: `${(caseStats.completed / caseStats.total) * 100}%`
                                  }}
                                  aria-valuenow={caseStats.completed}
                                  aria-valuemin="0"
                                  aria-valuemax={caseStats.total}
                                ></div>
                              </div>
                            </div>
                          )}
                          
                          <Row>
                            {batchCases.map(caseData => {
                              // Determine if case has been started (at least one answer submitted)
                              const isStarted = caseData.is_started;
                              const answeredCount = caseData.answered_question_count || 0;
                              const totalQuestions = caseData.total_question_count || 1;
                              const progressPercent = Math.round((answeredCount / totalQuestions) * 100);
                              
                              return (
                              <Col key={caseData.id} lg={6} md={12} className="mb-4">
                                <Card className={`h-100 border ${isStarted ? 'border-primary' : ''}`}>
                                  <Card.Header className={`text-center ${isStarted ? 'bg-primary text-white' : 'bg-light'}`}>
                                    <h6 className="mb-0 fw-bold">
                                      <i className={`fas me-2 ${isStarted ? 'fa-hourglass-half' : 'fa-user'}`}></i>
                                      Case ID: {caseData.id}
                                      {isStarted && (
                                        <span className="float-end ms-2">
                                          <i className="fas fa-spinner" style={{opacity: 0.7}}></i>
                                        </span>
                                      )}
                                    </h6>
                                  </Card.Header>
                                  <Card.Body className="text-center">
                                    {/* Assigned Date */}
                                    <div className="mb-4">
                                      <small className="text-muted">
                                        <i className="fas fa-calendar me-1"></i>
                                        Assigned: {formatDate(caseData.assignment?.assigned_at)}
                                      </small>
                                    </div>
                                    
                                    {/* Progress Bar - only show if started */}
                                    {isStarted && (
                                      <div className="mb-3">
                                        <small className="d-block text-muted mb-2">
                                          Progress: {answeredCount}/{totalQuestions} questions
                                        </small>
                                        <div className="progress" style={{height: '6px'}}>
                                          <div 
                                            className="progress-bar bg-primary" 
                                            role="progressbar" 
                                            style={{width: `${progressPercent}%`}}
                                            aria-valuenow={progressPercent} 
                                            aria-valuemin="0" 
                                            aria-valuemax="100"
                                          ></div>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Start/Continue Questions Button */}
                                    <div className="mt-auto">
                                      {isBatchAccessible(selectedBatch) ? (
                                        <Link
                                          to={`/doctor/answer-questions/start?protocol=${selectedProtocol?.id || caseData.protocol?.id || 35}&case=${caseData.id}&batch=${selectedBatch?.id}`}
                                          className={`btn btn-sm w-100 ${isStarted ? 'btn-primary' : 'btn-warning'}`}
                                        >
                                          <i className={`fas me-1 ${isStarted ? 'fa-play-circle' : 'fa-play'}`}></i>
                                          {isStarted ? 'Continue Reviewing' : 'Start Questions'}
                                        </Link>
                                      ) : (
                                        <Button variant="secondary btn-sm w-100" disabled>
                                          <i className="fas fa-lock me-1"></i>
                                          Batch Expired
                                        </Button>
                                      )}
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                              );
                            })}
                          </Row>
                        </>
                      ) : (
                        <div className="text-center py-3">
                          <i className="fas fa-info-circle fa-2x text-muted mb-2"></i>
                          <p className="text-muted">
                            Loading incomplete cases...
                          </p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}

                {!selectedProtocol && selectedBatch.protocols && selectedBatch.protocols.length > 0 && (
                  <Alert variant="info">
                    <i className="fas fa-info-circle me-2"></i>
                    Please select a protocol to view its cases.
                  </Alert>
                )}

                {selectedBatch && (!selectedBatch.protocols || selectedBatch.protocols.length === 0) && (
                  <Alert variant="warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    This batch doesn't have any protocols assigned to it yet.
                  </Alert>
                )}
              </div>
            ) : (
              <Card>
                <Card.Body className="text-center py-5">
                  <i className="fas fa-folder fa-4x text-muted mb-4"></i>
                  <h4 className="text-muted">Select a Batch</h4>
                  <p className="text-muted">
                    Choose a batch from the list to view its protocols and cases.
                  </p>
                </Card.Body>
              </Card>
            )}
          </div>
        </Row>
      </div>
    </ResearchLayout>
  );
};

export default DoctorBatchCases;